import React, { useEffect, useMemo, useState } from "react";

const CONSENT_VERSION = "1.0.0";
const STORE_KEY = "mikdok_cookie_consent";
const COOKIE_NAME = "mikdok_consent";

// ======= Design tokens (light/dark aware) =======
const useTheme = () => {
  const prefersDark =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;
  return prefersDark
    ? {
        bg: "rgba(17, 24, 39, 0.78)",
        card: "rgba(31, 41, 55, 0.78)",
        text: "#e5e7eb",
        muted: "#94a3b8",
        ring: "#60a5fa",
        border: "rgba(255,255,255,0.12)",
        btnBg: "#111827",
        btnText: "#e5e7eb",
        primary: "#3b82f6",
        shadow: "0 20px 60px rgba(0,0,0,.45)",
        blur: "blur(14px)",
      }
    : {
        bg: "rgba(255,255,255,0.78)",
        card: "rgba(255,255,255,0.86)",
        text: "#0f172a",
        muted: "#64748b",
        ring: "#2563eb",
        border: "rgba(0,0,0,0.08)",
        btnBg: "#ffffff",
        btnText: "#0f172a",
        primary: "#2563eb",
        shadow: "0 20px 60px rgba(2,6,23,.18)",
        blur: "blur(14px)",
      };
};

export default function CookieConsent() {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [functional, setFunctional] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  // 👇 responsive breakpoint: collapse to single-column UI on small screens
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const update = () => setIsNarrow(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    window.openCookieSettings = () => setOpen(true);
  }, []);

  useEffect(() => {
    const s = getStoredConsent();
    if (!s || s.version !== CONSENT_VERSION) setShowBanner(true);
    else applyConsent(s);
  }, []);

  const payload = useMemo(
    () => ({
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      source: "save_choices",
      essential: true,
      functional,
      analytics,
      marketing,
    }),
    [functional, analytics, marketing]
  );

  function acceptAll() {
    const p = {
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      source: "accept_all",
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    setStoredConsent(p);
    applyConsent(p);
    setShowBanner(false);
    setOpen(false);
    setFunctional(true);
    setAnalytics(true);
    setMarketing(true);
  }

  function rejectAll() {
    const p = {
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      source: "reject_all",
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
    };
    setStoredConsent(p);
    applyConsent(p);
    setShowBanner(false);
    setOpen(false);
    setFunctional(false);
    setAnalytics(false);
    setMarketing(false);
  }

  function saveChoices() {
    setStoredConsent(payload);
    applyConsent(payload);
    setShowBanner(false);
    setOpen(false);
  }

  function openSettings() {
    const s = getStoredConsent();
    if (s) {
      setFunctional(!!s.functional);
      setAnalytics(!!s.analytics);
      setMarketing(!!s.marketing);
    }
    setOpen(true);
  }

  // ======= Styles (glass + motion) =======
  const prefersReduced =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // ✅ center + max width; never edge-to-edge and never too narrow
  const bannerWrap = {
    position: "fixed",
    left: "50%",
    transform: "translateX(-50%)",
    bottom: 12,
    zIndex: 2147483647,
    width: "clamp(320px, 92vw, 640px)",
    backdropFilter: theme.blur,
    WebkitBackdropFilter: theme.blur,
    background: theme.bg,
    border: `1px solid ${theme.border}`,
    borderRadius: 16,
    boxShadow: theme.shadow,
    transition: prefersReduced ? "none" : "transform .3s ease, opacity .3s ease",
    opacity: 1,
  };

  // ✅ grid switches to single column on small screens
  const container = {
    margin: "0 auto",
    padding: "14px 16px",
    display: "grid",
    gridTemplateColumns: isNarrow ? "1fr" : "1fr auto",
    gap: 12,
    alignItems: "center",
  };

  const title = {
    margin: "0 0 6px",
    fontWeight: 800,
    color: theme.text,
    letterSpacing: ".2px",
    lineHeight: 1.2,
  };
  const body = {
    margin: 0,
    color: theme.muted,
    lineHeight: 1.45,
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  };
  const link = { color: theme.ring, textDecoration: "none" };

  // ✅ buttons stack on mobile, inline on desktop
  const actions = {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    justifySelf: isNarrow ? "stretch" : "end",
    flexDirection: isNarrow ? "column" : "row",
  };
  const btnBase = {
    appearance: "none",
    border: `1px solid ${theme.border}`,
    borderRadius: 999,
    padding: "10px 14px",
    background: theme.btnBg,
    color: theme.btnText,
    cursor: "pointer",
    fontWeight: 600,
    letterSpacing: ".15px",
    outline: "none",
    width: isNarrow ? "100%" : "auto",
  };
  const btn = { ...btnBase };
  const btnGhost = { ...btnBase, background: "transparent" };
  const btnPrimary = {
    ...btnBase,
    background: theme.primary,
    borderColor: theme.primary,
    color: "#fff",
    boxShadow: "0 4px 14px rgba(59,130,246,.25)",
  };
  const ring = { boxShadow: `0 0 0 3px ${hexToRgbA(theme.ring, 0.25)}` };

  const modalBackdrop = {
    position: "fixed",
    inset: 0,
    display: open ? "grid" : "none",
    placeItems: "center",
    background: "rgba(0,0,0,.45)",
    zIndex: 2147483647,
    padding: 12,
  };

  const modalCard = {
    width: "min(760px, 96vw)",
    background: theme.card,
    borderRadius: 18,
    border: `1px solid ${theme.border}`,
    boxShadow: theme.shadow,
    backdropFilter: theme.blur,
    WebkitBackdropFilter: theme.blur,
    color: theme.text,
    padding: 18,
  };

  return (
    <>
      {/* Banner */}
      {showBanner && (
        <div role="region" aria-label="Cookie banner" style={bannerWrap}>
          <div style={container}>
            <div style={{ minWidth: 0 }}>
              <p style={title}>We value your privacy</p>
              <p style={body}>
                We use cookies to run the site (essential) and—with your consent—for functional, analytics, and marketing.
                Manage your choices anytime. See our{" "}
                <a style={link} href="/cookies">
                  Cookie Policy
                </a>
                .
              </p>
            </div>
            <div style={actions}>
              <button
                style={btnGhost}
                onFocus={(e) => (e.currentTarget.style = { ...btnGhost, ...ring })}
                onBlur={(e) => (e.currentTarget.style = btnGhost)}
                onClick={openSettings}
              >
                Cookie settings
              </button>
              <button
                style={btn}
                onFocus={(e) => (e.currentTarget.style = { ...btn, ...ring })}
                onBlur={(e) => (e.currentTarget.style = btn)}
                onClick={rejectAll}
              >
                Reject all
              </button>
              <button
                style={btnPrimary}
                onFocus={(e) => (e.currentTarget.style = { ...btnPrimary, ...ring })}
                onBlur={(e) => (e.currentTarget.style = btnPrimary)}
                onClick={acceptAll}
              >
                Accept all
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-modal-title"
        style={modalBackdrop}
        onClick={(e) => {
          if (e.target === e.currentTarget) setOpen(false);
        }}
      >
        <div style={modalCard} role="document">
          <h2 id="cookie-modal-title" style={{ margin: "2px 0 8px", fontWeight: 800 }}>
            Cookie settings
          </h2>
          <p style={{ margin: "0 0 12px", color: theme.muted }}>
            Essential cookies are always on. Enable other categories as you like.
          </p>

          <Row theme={theme} title="Essential" subtitle="Authentication, security, load balancing">
            <Switch theme={theme} checked readOnly disabled />
          </Row>
          <Row theme={theme} title="Functional" subtitle="Remember language, UI helpers">
            <Switch theme={theme} checked={functional} onChange={setFunctional} />
          </Row>
          <Row theme={theme} title="Analytics" subtitle="Anonymous usage insights (GA4/Firebase)">
            <Switch theme={theme} checked={analytics} onChange={setAnalytics} />
          </Row>
          <Row theme={theme} title="Marketing" subtitle="Ads & conversion measurement (Google Ads)">
            <Switch theme={theme} checked={marketing} onChange={setMarketing} />
          </Row>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
            <button style={btn} onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button style={btnPrimary} onClick={saveChoices}>
              Save choices
            </button>
          </div>
          <div style={{ marginTop: 10, fontSize: ".92rem", color: theme.muted }}>
            You can revisit these settings anytime via <em>Cookie settings</em> in the footer.
          </div>
        </div>
      </div>
    </>
  );
}

// ======= Helpers/UI =======
function Row({ theme, title, subtitle, children }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 10,
        alignItems: "center",
        padding: "10px 0",
        borderBottom: `1px solid ${theme.border}`,
      }}
    >
      <div>
        <div style={{ fontWeight: 700 }}>{title}</div>
        <div style={{ color: theme.muted, fontSize: ".95rem" }}>{subtitle}</div>
      </div>
      {children}
    </div>
  );
}

function Switch({ theme, checked = false, onChange = () => {}, readOnly = false, disabled = false }) {
  const track = { position: "relative", width: 52, height: 30, display: "inline-block" };
  const input = { opacity: 0, width: 0, height: 0 };
  const bg = checked ? theme.ring : "rgba(148,163,184,.55)";
  const trackBg = {
    position: "absolute",
    inset: 0,
    background: bg,
    borderRadius: 999,
    transition: "all .22s",
    boxShadow: "inset 0 0 0 1px rgba(0,0,0,.05)",
    opacity: disabled ? 0.6 : 1,
  };
  const thumb = {
    position: "absolute",
    height: 24,
    width: 24,
    left: checked ? 26 : 4,
    top: 3,
    background: "#fff",
    borderRadius: "50%",
    boxShadow: "0 1px 3px rgba(0,0,0,.25)",
    transition: "all .22s",
  };

  return (
    <label style={track} aria-disabled={disabled}>
      <input
        type="checkbox"
        checked={checked}
        readOnly={readOnly}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={input}
        aria-checked={checked}
      />
      <span style={trackBg} />
      <span style={thumb} />
    </label>
  );
}

function getStoredConsent() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
function setStoredConsent(obj) {
  localStorage.setItem(STORE_KEY, JSON.stringify(obj));
  document.cookie = `${COOKIE_NAME}=1; Path=/; Max-Age=${3600 * 24 * 365}; SameSite=Lax; Secure`;
}
function applyConsent(state) {
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  gtag("consent", "update", {
    ad_storage: state.marketing ? "granted" : "denied",
    ad_user_data: state.marketing ? "granted" : "denied",
    ad_personalization: state.marketing ? "granted" : "denied",
    analytics_storage: state.analytics ? "granted" : "denied",
    functionality_storage: state.functional ? "granted" : "denied",
    security_storage: "granted",
  });

  if (window.firebase?.analytics) {
    try {
      const app = window.firebase.app();
      const analytics = window.firebase.analytics.getAnalytics(app);
      window.firebase.analytics.setAnalyticsCollectionEnabled(analytics, !!state.analytics);
    } catch {}
  }

  document.dispatchEvent(new CustomEvent("mikdok:consent-updated", { detail: state }));
}

function hexToRgbA(hex, a = 1) {
  if (!hex || typeof hex !== "string") return `rgba(0,0,0,${a})`;
  let c = hex.replace("#", "");
  if (c.length === 3) c = c.split("").map((x) => x + x).join("");
  const num = parseInt(c, 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r},${g},${b},${a})`;
}
